import { motion } from 'framer-motion'
import styled, { css } from 'styled-components/macro'
import { styleReset } from 'styles/misc'
import { theme } from 'styles/theme'
import { typography } from 'styles/typography'
import { fill, thumb, track } from './helpers'

export const SliderContainer = styled(motion.div)`
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: center;
  margin: 5rem 0;
`

export const Label = styled(motion.span)`
  display: block;
  position: relative;
  width: calc(100% - 2.8rem);
  margin-inline: 1.4rem;
  pointer-events: none;
`

export const LabelText = styled.span`
  display: block;
  width: max-content;
  transform: translateX(-50%);
`

export const ConfirmButton = styled.button`
  ${styleReset}
  ${typography.label}
  display: block;
  width: max-content;
  padding: 0.6em 1em;
  font-family: inherit;
  border-radius: 0.6rem;
  margin-inline: auto;
  color: ${theme.colors.background};
  background-color: ${theme.colors.text};

  &:not(:disabled) {
    cursor: pointer;
  }
`

export const SliderInput = styled.input.attrs({ type: 'range' })`
  --min: 0;
  --max: 100;
  --range: calc(var(--max) - var(--min));
  --ratio: calc((var(--val) - var(--min)) / var(--range));
  --sx: calc(0.5 *#{$thumb-d} + var(--ratio) * (100% - #{$thumb-d}));

  --thumb-size: 2.9rem;
  --track-height: 5px;
  --thumb-border-width: 3px;
  --accent: ${theme.colors.primary};

  &,
  &::-webkit-slider-thumb {
    -webkit-appearance: none;
  }

  margin: 1rem 0;
  padding: 0;
  width: 100%;
  height: 5px;
  background: transparent;
  font: 1em/1 arial, sans-serif;

  &::-webkit-slider-runnable-track {
    ${track};
    transform: translateY(-8px);
  }

  &::-moz-range-track {
    ${track};
  }

  &::-ms-track {
    ${track};
  }

  &::-moz-range-progress {
    ${fill};
  }

  &::-ms-fill-lower {
    ${fill};
  }

  &::-webkit-slider-thumb {
    margin-top: calc(0.5 * (5px - var(--thumb-size)));
    ${thumb};
  }

  &::-moz-range-thumb {
    ${thumb};
  }
  &::-ms-thumb {
    margin-top: 0;
    ${thumb};
  }

  &::-ms-tooltip {
    display: none;
  }

  &:not(:disabled) {
    cursor: pointer;
  }

  &:disabled {
    --thumb-size: 1.2rem;
    --thumb-border-width: 0;

    opacity: 1;

    &::-webkit-slider-thumb {
      background: var(--accent);
    }
    &::-moz-range-thumb {
      background: var(--accent);
    }
    &::-ms-thumb {
      background: var(--accent);
    }
  }
`

export const StaticLabel = styled(Label)<{
  $align: 'left' | 'right'
  $verticalAlign: 'top' | 'bottom'
  $accent: 'primary' | 'success'
}>`
  font-size: 1.4rem;
  padding: 1px 0;
  color: ${(props) =>
    props.$accent === 'primary' ? theme.colors.primary : theme.colors.success};

  ${(props) =>
    props.$verticalAlign === 'top'
      ? css`
          margin-bottom: -3rem;
        `
      : css`
          margin-top: -3.2rem;
          justify-content: end;
        `}

  ${LabelText} {
    display: flex;
    flex-direction: column;
    height: 100%;
    line-height: 1.2;
    margin-block: -0.2em 0;
    padding: ${(props) =>
      props.$verticalAlign === 'top' ? '0 0.4em 1em' : ' 1em 0.4em 0'};
    transform: ${(props) =>
      props.$align === 'left' ? 'none' : 'translateX(-100%)'};

    ${(props) =>
      props.$verticalAlign === 'bottom' &&
      css`
        justify-content: end;
      `}

    ${(props) =>
      props.$align === 'left'
        ? css`
            border-left: 2px solid currentColor;
            margin-left: -1px;
          `
        : css`
            border-right: 2px solid currentColor;
            margin-left: 3px;
            text-align: right;
          `}
  }
`

export const Dot = styled(motion.span)`
  position: absolute;
  display: block;
  width: 100%;
  margin-top: -1rem;
  transform-origin: 0 50%;

  &::before {
    content: '';
    display: block;
    width: 1.2rem;
    height: 1.2rem;
    border-radius: 50%;
    transform: translateX(-50%);
    background-color: ${theme.colors.success};
  }
`
